<template>
  <div class="d-flex align-center bulk-selector__container">
    <v-checkbox-btn
      density="compact"
      :model-value="allItemsSelected"
      :indeterminate="isIndeterminate"
      @click.prevent.stop="handleCheckboxClick"
    ></v-checkbox-btn>

    <v-menu v-if="showMenu">
      <template v-slot:activator="{ props }">
        <v-icon icon="$triangle" v-bind="props" class="item-clickable" />
      </template>
      <v-list>
        <v-list-item v-for="action in actions" :key="action" @click="handleMenuAction(action)">
          <v-list-item-title>{{ $t(`selection.${action}`) }}</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { SelectorValue } from "@/constants/selector.ts";

export default defineComponent({
  props: {
    /**
     * Determines if the checkbox shows a dash [-].
     * Passed from composable 'isIndeterminate'.
     */
    isIndeterminate: {
      type: Boolean,
      required: true,
    },
    /**
     * Determines if the checkbox shows a check mark [x].
     * Passed from composable 'allItemsSelected'.
     */
    allItemsSelected: {
      type: Boolean,
      required: true,
    },
    /**
     * Determines if all items on page are selected.
     * Passed from composable 'allItemsOnPageSelected'.
     */
    allItemsOnPageSelected: {
      type: Boolean,
      required: true,
    },
    /**
     * The items currently visible on the page (used for Select/Deselect Items payload).
     */
    items: {
      type: Array,
      required: true,
    },
    showMenu: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["selection-changed"],
  setup(props, { emit }) {
    const actions = [SelectorValue.SELECT_ALL_PAGES, SelectorValue.DESELECT_ALL_PAGES];

    /**
     * Handles the main checkbox click (toggle current page).
     */
    const handleCheckboxClick = () => {
      const shouldDeselect = props.allItemsSelected || props.allItemsOnPageSelected;

      emit("selection-changed", {
        selectorValue: shouldDeselect ? SelectorValue.DESELECT_ITEMS : SelectorValue.SELECT_ITEMS,
        items: props.items,
      });
    };

    /**
     * Handles the dropdown actions (Global Select/Deselect).
     */
    const handleMenuAction = (action: string) => {
      emit("selection-changed", {
        selectorValue: action,
        items: props.items,
      });
    };

    return {
      handleMenuAction,
      handleCheckboxClick,
      actions,
    };
  },
});
</script>

<style scoped lang="scss">
.bulk-selector__container {
  width: 33px;
}
</style>
